import React, { useState, useCallback, useRef } from "react";
import LabelText from "../../ui/LabelText";
import CloseIcon from "@mui/icons-material/Close";
import { ForawrdBackwardButton } from "../../styled-components/PageHeaderStyles";
import SearchInput from "../../ui/SearchInput";
import { useSidebar } from "../../contexts/SidebarContext";
import { fetchData } from "../../services/api";
import handleApiResponse from "../../utils/handleApiResponse";
import IApiResponse from "../../types/IApiResponse";
import apiUrls from "../../config/apiUrls";
import { styled } from "styled-components";
import { debounceFunction } from "../../utils/debounceFunction";
import GlobalSearchList from "./GlobalSearchList";
import { useLoading } from "../../contexts/LoadingContext";
import { ModuleSpinner } from "../../ui/LoadingSpinner/ModuleSpinner";
import { filterWomensFootball } from "../../utils/womensFootballFilter";


const DrawerContainer = styled.div`
  background: ${({ theme }) => theme.playerContentBackground};
`;

interface IGlobalSearchData {
  id: number;
  imageLogoURL: string;
  isTeam: boolean;
  isAttributesFound: boolean;
  name: string;
  subName: string;
}

const GlobalSearch = () => {
  const { toggleSidebar, toggleDrawer } = useSidebar();
  const { moduleLoading, setModuleLoading } = useLoading();
  const [globalSearchData, setGlobalSearchData] = useState<IGlobalSearchData[]>([]);
  const [globalSearchText, setGlobalSearchText] = useState<string>("");
  const [clearInput, setClearInput] = useState(false);
  const [searchInitiated, setSearchInitiated] = useState(false);
  const [isData, setIsData] = useState<boolean>(false);
  const [searchItemsFromStorage, setSearchItemsFromStorage] = useState<number[]>(JSON.parse(localStorage.getItem("searchItem")!)?.reverse());
  const clearPreviousSearches = () => {
    localStorage?.removeItem("searchItem");
    setSearchItemsFromStorage([]);
  };

  const renderGlobalSearchResult = () => {
    if (searchInitiated && globalSearchData.length === 0 && isData) {
      return (
        <div className="no-result-data">
          <LabelText>No results found</LabelText>
        </div>
      );
    }
    if (globalSearchData.length === 0 && searchItemsFromStorage?.length > 0) {
      return (
        <div>
          <div className="previous-search-header">
            <p>Previous searches</p>
            <p onClick={() => clearPreviousSearches()}>Clear</p>
          </div>
          <div className="player-profile-list">
            <GlobalSearchList
              searchData={searchItemsFromStorage}
              searchText={""}
              multiSelection={false}
              iconType="image"
              displayCheckBox={false}
              ifEntireNameHiglight={true}
            />
          </div>
        </div>
      );
    }
    return (
      globalSearchData.length > 0 && (
        <div className="player-profile-list">
          <GlobalSearchList
            searchData={globalSearchData}
            searchText={globalSearchText}
            multiSelection={false}
            iconType="image"
            displayCheckBox={false}
          />
        </div>
      )
    );
  };

  const handleClose = () => {
    toggleDrawer?.(false);
    toggleSidebar?.(false);
    setGlobalSearchText("");
    setGlobalSearchData([]);
    setSearchInitiated(false);
    setIsData(false);
  };
  const abortControllerRef = useRef<AbortController | null>(null);
  const handleSearch = useCallback(
    debounceFunction(async (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
       // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new AbortController for the current request
    const controller = new AbortController();
    abortControllerRef.current = controller;
      if (value.length >= 3) {
        setGlobalSearchText(value);
        setSearchInitiated(true);
        setModuleLoading(true);
        try {
          const response: IApiResponse<IGlobalSearchData[]> = await fetchData(
            `${apiUrls["GetTeamPlayerGlobalSearch"]}?queryString=${value}`,
            controller.signal
          );
          handleApiResponse(
            response,
            (GlobalSearchData) => {
              setModuleLoading(false);
              const filtered = filterWomensFootball(GlobalSearchData);
              if (filtered.length === 0) setIsData(true);
              else {
                setIsData(false);
                setGlobalSearchData(filtered);
              }
            },

            (error) => {
              console.error("Error fetching Player Search Data:", error);
                setModuleLoading(false);

            }
          );
        } catch (error) {
          console.error("Error fetching Player Search Data:", error);
          setModuleLoading(false);
        } finally {
          setModuleLoading(false);
        }
      } else {
        setGlobalSearchData([]);
        setSearchInitiated(false);
        setIsData(false);
      }
    }, 300),
    []
  );

  const handleClear = () => {
    setGlobalSearchText("");
    setGlobalSearchData([]);
    setSearchInitiated(false);
    setIsData(false);
  };

  return (
    <DrawerContainer className="main-drawer-container">
      <div className="drawer-header">
        <LabelText className="drawer-title">Search</LabelText>
        <div className="header-buttons">
          <ForawrdBackwardButton onClick={handleClose}>
            <CloseIcon className="cta-btn-icons" />
          </ForawrdBackwardButton>
        </div>
      </div>
      <div className="player-search-data">
        <div className="container active">
          <SearchInput
            handleSearch={handleSearch}
            handleClear={handleClear}
            placeholder="Player and club pages"
            fontSize="32px"
            type="SearchPlayer"
            clearInput={clearInput}
            autoFocus={false}
          />
        </div>
        {renderGlobalSearchResult()}
      </div>
      {moduleLoading && <ModuleSpinner />}
    </DrawerContainer>
  );
};

export default GlobalSearch;
